import { useParams } from "react-router-dom";
import { NavLink} from "react-router-dom";
import React, { useState, useEffect, useRef } from 'react'
import { Flex, Divider, View, Text, Heading, Button, SelectField} from '@aws-amplify/ui-react';
import { API, Auth } from 'aws-amplify';
import Loadingmini from '../components/loadingmini';
import SaveViewModal from "./saveViewModal";
import { FaSave, FaTimes } from 'react-icons/fa';

export default function TableViewSelector({tableType, gridRef, updateTableColumns, updateTablePivotMode, displaySideDock, selectObject, selectedFleets, configReady = true} ) {
    let params = useParams();
    const normalizedFleets = React.useMemo(() => {
        if (Array.isArray(selectedFleets)) return selectedFleets;
        if (typeof selectedFleets === "string" && selectedFleets.length > 0) {
            return [selectedFleets];
        }
        return [];
    }, [selectedFleets]);
    const [is_loading, updateIsLoading] = React.useState(true);
    const [displayTableTypeSelect, updateDisplayTableTypeSelect] = React.useState(false);
    const [tableSelectedViewName, updateTableSelectedViewName] = React.useState("");
    const [tableSelectedViewId, updateTableSelectedViewId] = React.useState(null);
    const [tableSelectedViewCanDelete, updateTableSelectedViewCanDelete] = React.useState(false);
    const [showSaveViewForm, updateShowSaveViewForm] = React.useState(false);
    const [showDeleteViewForm, updateShowDeleteViewForm] = React.useState(false);
    const [newViewName, updateNewViewName] = React.useState("");
    const [displayErrorColumnStateTooLong, updateDisplayErrorColumnStateTooLong] = React.useState(false);
    const [isFavoriteView, updateIsFavoriteView] = React.useState(false); 
    const [tableViews, updateTableViews] = React.useState([{}]);
    const [pendingSavePayload, setPendingSavePayload] = useState(null);
    const modalStyles = {
        overlay: {
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "blur(2px)"
        },
        modal: {
            background: "white",
            padding: "30px",
            borderRadius: "12px",
            minWidth: "400px",
            maxWidth: "90vw",
            boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
        }
    };
    const COL_KEY_MAP = {
        field: "f",
        headerName: "h",
        width: "w",
        pinned: "p",
        sort: "s",
        sortIndex: "si",
        rowGroup: "rg",
        rowGroupIndex: "rgi",
        pivot: "pv",
        pivotIndex: "pvi",
        aggFunc: "af",
        allowedAggFuncs: "aaf",
        enablePivot: "ep",
        enableRowGroup: "erg"
    };
    
    // reverse map
    const COL_KEY_MAP_REVERSE = Object.fromEntries(
        Object.entries(COL_KEY_MAP).map(([k,v]) => [v,k])
    );
    const formatSeconds = (totalSeconds) => {
        if (totalSeconds == null || isNaN(totalSeconds)) return "";
    
        const isNegative = totalSeconds < 0;
        let seconds = Math.abs(Math.floor(totalSeconds));
    
        const days = Math.floor(seconds / 86400);
        seconds %= 86400;
    
        const hours = Math.floor(seconds / 3600);
        seconds %= 3600;
    
        const minutes = Math.floor(seconds / 60);
        seconds %= 60;
    
        const parts = [];
        if (days) parts.push(`${days}d`);
        if (hours) parts.push(`${hours}h`);
        if (minutes) parts.push(`${minutes}m`);
        if (seconds || parts.length === 0) parts.push(`${seconds}s`);
    
        return `${isNegative ? "-" : ""}${parts.join(" ")}`;
    };

    function compressCols(viewJson) {
        if (!viewJson?.columns) return viewJson;
    
        return {
            ...viewJson,
            columns: viewJson.columns.map(col => {
                const newCol = {};
                Object.entries(col).forEach(([key, val]) => {
                    const shortKey = COL_KEY_MAP[key] || key;
                    newCol[shortKey] = val;
                });
                return newCol;
            })
        };
    }

    function expandColsIfNeeded(viewJson) {
        if (!viewJson?.columns?.length) return viewJson;
    
        // detect compression (look for abbreviated key)
        const isCompressed =
            viewJson.columns.some(col =>
                Object.keys(col).some(k => COL_KEY_MAP_REVERSE[k])
            );
    
        if (!isCompressed) return viewJson;
    
        return {
            ...viewJson,
            columns: viewJson.columns.map(col => {
                const newCol = {};
                Object.entries(col).forEach(([key, val]) => {
                    const longKey = COL_KEY_MAP_REVERSE[key] || key;
                    newCol[longKey] = val;
                });
                return newCol;
            })
        };
    }
    
    const formatPercent = (value) => {
        if (value == null || isNaN(value)) return "";
    
        let v = Number(value);
    
        // detect likely already-percent values
        // (only safe heuristic: > 1 AND < 100)
        const looksLikePercent = v > 1 && v <= 100;
    
        if (!looksLikePercent) {
            v = v * 100;
        }
    
        return `${Math.round(v)}%`;
    };

    
    function isFooterRow(params) {
        return (
            params.node?.footer ||
            params.node?.rowPinned === "bottom" ||
            !params.data
        );
    }
    
    function applyAutoFormatting(col) {
        const field = col.field?.toLowerCase();
    
        // percent fields
        if (field === "percent_late_night_hours") {
            return {
                ...col,
                valueFormatter: (params) => formatPercent(params.value),
                type: "numericColumn"
            };
        }
    
        // safety score
        if (field === "safety_score") {
            return {
                ...col,
                valueFormatter: (params) =>
                    params.value == null ? "" : Math.round(params.value),
                type: "numericColumn"
            };
        }
    
        // miles driven specifically
        if (field === "total_miles") {
            return {
                ...col,
                valueFormatter: (params) =>
                    params.value == null ? "" : Math.round(params.value),
                type: "numericColumn"
            };
        }
    
        // Rental Days
        if (field === "rental_days") {
            return {
                ...col,
                valueFormatter: (params) =>
                    params.value == null ? "" : Math.round(params.value),
                type: "numericColumn"
            };
        }

        // anything ending in miles
        if (field?.endsWith("_miles") || field?.endsWith("_mls")) {
            return {
                ...col,
                valueFormatter: (params) =>
                    params.value == null ? "" : Math.round(params.value),
                type: "numericColumn"
            };
        }
    
        // distance from homespot
        if (field === "distance") {
            return {
                ...col,
                valueFormatter: (params) =>
                    params.value == null ? "" : Math.round(params.value),
                type: "numericColumn"
            };
        }

        // position timestamp
        if (field === "position_timestamp") {
            return {
                ...col,
                valueFormatter: (params) =>
                    params.value == null ? "" : Math.round(params.value),
                type: "numericColumn"
            };
        }

        // generic: round numbers with > 2 decimals to nearest hundredth
        /*
        if (col.type === "numericColumn" || typeof col.valueFormatter === "undefined") {
            return {
                ...col,
                valueFormatter: (params) => {
                    const v = params.value;
                    if (v == null || typeof v !== "number") return v;

                    // check decimal precision
                    const decimals = (v.toString().split(".")[1] || "").length;

                    if (decimals > 2) {
                        return Math.round(v * 100) / 100;
                    }

                    return v;
                },
                type: "numericColumn"
            };
        }
        */

        return col;
    }

    function applyView(selectedView) {
        updateTableSelectedViewId(selectedView.id);
        updateTableSelectedViewName(selectedView.title);
        updateIsFavoriteView(selectedView.is_favorite > 0);
        updateTableSelectedViewCanDelete(selectedView.all_users === 0);
        updateTablePivotMode(selectedView.pivot_mode_enabled > 0);
    
        let viewJson;
        try {
            viewJson =
                typeof selectedView.view_json === "string"
                    ? JSON.parse(selectedView.view_json)
                    : selectedView.view_json;
        } catch (e) {
            console.error("Error parsing view_json:", e);
            return;
        }
    
        viewJson = expandColsIfNeeded(viewJson);
        if (!viewJson?.columns) return;
    


        const cols = viewJson.columns.map((col) => {
            let singularLink = tableType === "vehicles" ? "vehicle" : "user";
    
            // clickable ID / car_id column
            if (col.field === "id" || col.field === "car_id") {
                var fleetQuery = normalizedFleets.length
                    ? `?fleets=${normalizedFleets.join(",")}`
                    : "";
                    return {
                        ...col,
                        cellRenderer: (params) =>
                            isFooterRow(params)
                                ? params.value
                                : displaySideDock
                                    ? (
                                        <Text>
                                            <Button
                                                style={{
                                                    border: "none",
                                                    color: "#0000EE",
                                                    fontStyle: "italic",
                                                    marginLeft: "-15px",
                                                }}
                                                onClick={() => selectObject(params.value)}
                                            >
                                                {params.value}
                                            </Button>
                                        </Text>
                                    )
                                    : (
                                        <NavLink to={`/${singularLink}/${params.value}${fleetQuery}`}>
                                            {params.value}
                                        </NavLink>
                                    ),
                    };
            }
    
            // clickable policy_id column
            if (col.field === "policy_id") {
                return {
                    ...col,
                    cellRenderer: (params) =>
                        isFooterRow(params)
                            ? params.value
                            : (
                                <NavLink to={`/policy/${params.value}`}>
                                    {params.value}
                                </NavLink>
                            ),
                };
            }

            
            // numeric formatting for vehicle_position_same
            if (col.field === "vehicle_position_same") {
                return {
                    ...col,
                    valueFormatter: (params) => formatSeconds(params.value),
                    type: "numericColumn",
                };
            }

            // default
            return applyAutoFormatting(col);
        });
    
        updateTableColumns(cols);
    
        requestAnimationFrame(() => {
            const api = gridRef?.current?.api;
            if (!api) return;
        
            // 1. clear ALL grouping + pivot
            api.applyColumnState({
                defaultState: {
                    rowGroup: false,
                    pivot: false,
                    sort: null
                }
            });
        
            // 2. apply saved view
            api.applyColumnState({
                state: cols.map(c => ({
                    colId: c.field,
                    rowGroup: c.rowGroup,
                    rowGroupIndex: c.rowGroupIndex,
                    pivot: c.pivot,
                    pivotIndex: c.pivotIndex
                })),
                applyOrder: true
            });

            // 3. apply sort
            api.applyColumnState({
                state: cols.map(c => ({
                    colId: c.field,
                    sort: c.sort,
                    sortIndex: c.sortIndex
                }))
            });

            /*
            // 4. APPLY FILTERS 
            if (selectedView.view_filters) {
                try {
                    const filters =
                        typeof selectedView.view_filters === "string"
                            ? JSON.parse(selectedView.view_filters)
                            : selectedView.view_filters;

                    api.setFilterModel(filters);
                } catch (e) {
                    console.error("Error applying filters:", e);
                }
            } else {
                api.setFilterModel(null);
            }
            */
            requestAnimationFrame(() => {
                if (selectedView.view_filters) {
                    try {
                        const filters =
                            typeof selectedView.view_filters === "string"
                                ? JSON.parse(selectedView.view_filters)
                                : selectedView.view_filters;
        
                        api.setFilterModel(filters);
                    } catch (e) {
                        console.error("Error applying filters:", e);
                    }
                } else {
                    api.setFilterModel(null);
                }
            });
            
        });
    }


    async function getUserPivotTableConfig(skipSetToFirst = false) {
        updateIsLoading(true);

        // 1. Fetch auth/session
        let session = await Auth.currentSession();
        let accessToken = session.getAccessToken();
        const jwt = accessToken.getJwtToken();

        const myInit = {
            queryStringParameters: {
                back_end: process.env.REACT_APP_BACKEND,
                jwt,
                api_endpoint: "api3:staff:table_views",
                request_type: "GET",
                table_type: tableType
            }
        };

        let res;
        try {
            res = await API.get("adminWebTools", "/ago", myInit);
        } catch (e) {
            console.log("userpivottableconfig error:", e);
            updateIsLoading(false);
            return;
        }

        // ---------------------------
        // (B) Extract views
        // ---------------------------
        const views = Array.isArray(res.resp) ? res.resp : [];
        updateTableViews(views);

        if (views.length === 0) {
            console.log("No views available; use default");
            updateIsLoading(false);
            return;
        }

        let selectedView;

        // CASE 2 — skipSetToFirst === true → pick newest view automatically
        if (skipSetToFirst) {
            selectedView = views.reduce((latest, v) =>
                !latest || new Date(v.sql_datetime) > new Date(latest.sql_datetime)
                    ? v
                    : latest,
                null
            );
        }

        // CASE 1 — initial load → pick first view
        /*
        if (!skipSetToFirst) {
            selectedView = views[0];
        }
        */
        const sortedViews = [...views].sort((a, b) => {
            if ((b.is_favorite || 0) !== (a.is_favorite || 0)) {
                return (b.is_favorite || 0) - (a.is_favorite || 0);
            }
            if (a.id === 1 && b.id !== 1) return -1;
            if (b.id === 1 && a.id !== 1) return 1;
            return (a.title || "").localeCompare(b.title || "");
        });
        
        if (!skipSetToFirst) {
            selectedView = sortedViews[0];
        }

        // (optional future CASE 3 — select specific ID)
        if (!selectedView) {
            updateIsLoading(false);
            return;
        }
        applyView(selectedView);
        updateIsLoading(false);
    }

    useEffect(() => {
        if (configReady) {
            getUserPivotTableConfig();
        }
      }, [configReady])

    useEffect(() => {
        if (!gridRef || !gridRef.current) return;
        if (gridRef.current?.api && gridRef.current?.columnApi) {
            console.log("Grid is ready!");
        }
    }, [gridRef]);

    useEffect(() => {
        if (!tableSelectedViewId || tableViews.length === 0) return;
        const selectedView = tableViews.find(
            (v) => v.id === tableSelectedViewId
        );
        if (selectedView) {
            applyView(selectedView);
        }
    }, [displaySideDock]);

    async function deleteView() {
        if(tableSelectedViewCanDelete)
        {
            updateIsLoading(true)
            let session = await Auth.currentSession()      
            let accessToken = session.getAccessToken()
            var jwt = accessToken.getJwtToken()
            var myInit = {queryStringParameters:{ 
                "back_end": process.env.REACT_APP_BACKEND,
                "jwt":jwt,
                "api_endpoint": "api3:staff:table_views",
                "request_type":"POST",
                "table_type":tableType,
                "action":"delete",
                "view_id":tableSelectedViewId
            }};
            console.log(JSON.stringify(myInit))
            try {
                var res = await API.get('adminWebTools','/ago',myInit)
                console.log(JSON.stringify(res))
                updateShowSaveViewForm(false)
                updateDisplayTableTypeSelect(false)
                updateShowDeleteViewForm(false)
                getUserPivotTableConfig()
            }
            catch (e) {
                var res = {status:"Error pivottableconfig - email us at developer@agorisk.com: "+toString(e),resp:[]}
                console.log('userpivottableconfig post error on id '+tableSelectedViewId+': '+JSON.stringify(res))
                updateIsLoading(false)
                updateShowDeleteViewForm(false)
                return;
            }
        }
    }


    function transformAgGridStateToColumns(
        state,
        pivotColumns = null,
        rowGroups = null,
        valueColumns = null,
        pivotModeEnabled = false
    ) {
        const outputColumns = [];
    
        // Create a map from colId → column for fast lookup
        const stateMap = new Map(state.map(col => [col.colId, col]));
    
        // Build set of allowed fields when pivot mode is enabled
        let allowedFields = null;
        if (pivotModeEnabled) {
            console.log('assembling pivot view...')
            pivotColumns = pivotColumns || [];
            rowGroups = rowGroups || [];
            valueColumns = valueColumns || [];
            allowedFields = new Set([...pivotColumns, ...rowGroups, ...valueColumns]);
    
            // Only iterate over allowed fields
            for (const colId of allowedFields) {
                const col = stateMap.get(colId);
                if (!col) continue; // Skip if not found in state
    
                // Skip hidden columns
                const isRowGroup = rowGroups?.includes(colId);
                if (col.hide === true && !isRowGroup) continue;
                const newCol = {
                    field: colId,
                    headerName: colId.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
                    allowedAggFuncs: col.allowedAggFuncs,
                    aggFunc: col.aggFunc,
                    ...(col.width && col.width!==100 ? { width: col.width } : {}),
                    ...(col.pinned ? { pinned: col.pinned } : {}),
                    ...(col.enablePivot ? { enablePivot: col.enablePivot } : {}),
                    ...(col.enableRowGroup && col.enableRowGroup!==false ? {} : { enableRowGroup: col.enableRowGroup }),
                    ...(col.sort ? { sort: col.sort } : {}),
                    ...(col.sortIndex !== undefined ? { sortIndex: col.sortIndex } : {})
                };
                if (isRowGroup) {
                    newCol.rowGroup = true;
                    newCol.rowGroupIndex = rowGroups.indexOf(colId);
                }
                if (col.pivotIndex !== undefined && col.pivotIndex !== null) {
                    newCol.pivotIndex = col.pivotIndex;
                }
                
                outputColumns.push(newCol);
            }
        } else {
            // Pivot mode disabled: iterate all columns as usual
            console.log('assembling non-pivot view...')
            for (const col of state) {
                const colId = col.colId;
                if (!colId) continue;
                if (colId === "ag-Grid-SelectionColumn" || colId === "ag-Grid-AutoColumn") continue;
                const isRowGroup = rowGroups?.includes(colId);
                if (col.hide === true && !isRowGroup) continue;
                const newCol = {
                    field: colId,
                    headerName: colId.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
                    allowedAggFuncs: col.allowedAggFuncs,
                    aggFunc: col.aggFunc,
                    ...(col.width && col.width!==100 ? { width: col.width } : {}),
                    ...(col.pinned ? { pinned: col.pinned } : {}),
                    ...(col.enablePivot ? { enablePivot: col.enablePivot } : {}),
                    ...(col.enableRowGroup && col.enableRowGroup!==false ? {} : { enableRowGroup: col.enableRowGroup }),
                    ...(col.sort ? { sort: col.sort } : {}),
                    ...(col.sortIndex !== undefined ? { sortIndex: col.sortIndex } : {})
                };
                if (isRowGroup) {
                    newCol.rowGroup = true;
                    newCol.rowGroupIndex = rowGroups.indexOf(colId);
                }
                if (col.pivotIndex !== undefined && col.pivotIndex !== null) {
                    newCol.pivotIndex = col.pivotIndex;
                }
    
                outputColumns.push(newCol);
            }
        }
        // ensure rowGroup columns are always included
        if (rowGroups && rowGroups.length > 0) {
            const existingFields = new Set(outputColumns.map(c => c.field));

            rowGroups.forEach((groupField, index) => {
                if (!existingFields.has(groupField)) {
                    outputColumns.push({
                        field: groupField,
                        headerName: groupField
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, c => c.toUpperCase()),
                        rowGroup: true,
                        rowGroupIndex: index
                    });
                }
            });
        }
        return { columns: outputColumns };
    }
    

    async function saveView() {
        console.log('saveView...')
        if (!pendingSavePayload) return;
        if (!newViewName || !newViewName.trim()) {
            alert("Please name your view before saving.");
            return;
        }
        console.log('pendingSavePayload found...')
        updateShowSaveViewForm(false);
        updateDisplayTableTypeSelect(false);
        updateIsLoading(true);

        const session = await Auth.currentSession();
        const jwt = session.getAccessToken().getJwtToken();

        const myInit = {
            queryStringParameters: {
                back_end: process.env.REACT_APP_BACKEND,
                jwt,
                api_endpoint: "api3:staff:table_views",
                request_type: "POST",
                table_type: tableType,
                action: "create",
                title: newViewName,
                view_json: JSON.stringify(compressCols(pendingSavePayload.view_json)),
                view_pivot_columns: JSON.stringify(pendingSavePayload.pivotColumns),
                view_row_groups: JSON.stringify(pendingSavePayload.rowGroups),
                pivot_mode_enabled: pendingSavePayload.pivotModeEnabled,
                view_filters: Object.keys(pendingSavePayload.filterModel || {}).length ? JSON.stringify(pendingSavePayload.filterModel) : null
            }
        };

        try {
            await API.post("adminWebTools", "/ago", myInit);
            console.log('success')
            updateShowSaveViewForm(false);
            updateDisplayTableTypeSelect(false);
            getUserPivotTableConfig(true);
            console.log('payload: '+JSON.stringify(pendingSavePayload))
        } catch (e) {
            console.error("Save view failed:", e);
            console.log(JSON.stringify(pendingSavePayload.view_json))
            updateIsLoading(false);
        }
    }


    async function makeFavoriteView() {
        updateIsFavoriteView(true)
        let session = await Auth.currentSession()      
        let accessToken = session.getAccessToken()
        var jwt = accessToken.getJwtToken()
        var myInit = {queryStringParameters:{ 
            "back_end": process.env.REACT_APP_BACKEND,
            "jwt":jwt,
            "api_endpoint": "api3:staff:table_views",
            "request_type":"POST",
            "table_type":tableType,
            "action":"favorite",
            "view_id":tableSelectedViewId
        }};
        try {
            var res = await API.post('adminWebTools','/ago',myInit)
            console.log(JSON.stringify(res))
            getUserPivotTableConfig()
        } catch (e) {
            var res = {status:"Error pivottableconfig - email us at developer@agorisk.com: "+toString(e),resp:[]}
            console.log('userpivottableconfig post error on id '+tableSelectedViewId+': '+JSON.stringify(res))
        }
    }

    function saveGridState() {
        if (!gridRef?.current) return;
    
        const columnState = gridRef.current.api.getColumnState();
        const isPivotModeEnabled = gridRef.current.api.isPivotMode();
        const pivotColumns = gridRef.current.api.getPivotColumns().map(c => c.colId);
        const rowGroups = gridRef.current.api.getRowGroupColumns().map(c => c.colId);
        const valueColumns = gridRef.current.api.getValueColumns().map(c => c.colId);
        const filterModel = gridRef.current.api.getFilterModel();

        /*
        try{
            console.log(JSON.stringify(filterModel))
        } catch (e) {
            console.log('error getting filter model: ' + JSON.stringify(e))
        }
        */

        if (isPivotModeEnabled && columnState.length > 1000) {
            updateDisplayErrorColumnStateTooLong(true);
            return;
        }
    
        const viewJson = transformAgGridStateToColumns(
            columnState,
            pivotColumns,
            rowGroups,
            valueColumns,
            isPivotModeEnabled
        );
    
        setPendingSavePayload({
            view_json: viewJson,
            pivotColumns,
            rowGroups,
            pivotModeEnabled: isPivotModeEnabled,
            filterModel
        });
    
        updateShowSaveViewForm(true);
    }
    
    function retryNewView()
    {
        updateShowSaveViewForm(false);
        updateDisplayTableTypeSelect(false);
    }

    const renderViewFolderTabs = () => {
        if (!tableViews || tableViews.length === 0) return null;
    
        const sortedViews = [...tableViews].sort((a, b) => {
            if ((b.is_favorite || 0) !== (a.is_favorite || 0)) {
                return (b.is_favorite || 0) - (a.is_favorite || 0);
            }
            if (a.id === 1 && b.id !== 1) return -1;
            if (b.id === 1 && a.id !== 1) return 1;
            return (a.title || "").localeCompare(b.title || "");
        });
    
        return (
            <View
                style={{
                    marginTop: "10px",
                    borderBottom: "1px solid #ddd",
                }}
            >
                <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    style={{
                        overflowX: "auto",
                        whiteSpace: "nowrap",
                    }}
                >
                    {/* LEFT: folder tabs */}
                    <Flex gap="0.25rem" style={{ flexWrap: "nowrap" }}>
                        {sortedViews.map((view) => {
                            const isActive = view.id === tableSelectedViewId;
        
                            return (
                                <View
                                    key={view.id}
                                    onClick={() => {
                                        updateIsLoading(true);
                                        applyView(view);
                                        updateIsLoading(false);
                                    }}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "8px 14px",
                                        cursor: "pointer",
                                        borderTopLeftRadius: "8px",
                                        borderTopRightRadius: "8px",
                                        border: "1px solid #ccc",
                                        borderBottom: isActive ? "none" : "1px solid #ccc",
                                        background: isActive ? "#fff" : "#f3f3f3",
                                        fontWeight: isActive ? "600" : "400",
                                        position: "relative",
                                        top: "1px",
                                        whiteSpace: "nowrap",
                                        userSelect: "none"
                                    }}
                                >
                                    {view.title}

                                    {/* DELETE ICON */}
                                    {view.all_users === 0 && (
                                        <View
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                updateTableSelectedViewId(view.id);
                                                updateTableSelectedViewName(view.title);
                                                updateTableSelectedViewCanDelete(view.all_users === 0);
                                                updateShowDeleteViewForm(true);
                                            }}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                marginLeft: "4px",
                                                color: "#999",
                                                cursor: "pointer",
                                                fontSize: "12px"
                                            }}
                                            title="Delete view"
                                        >
                                            <FaTimes />
                                        </View>
                                    )}
                                </View>
                            );
                        })}
                    </Flex>
        
                    {/* RIGHT: save button */}
                    <Button
                        onClick={saveGridState}
                        title="Save current view"
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "18px",
                            padding: "6px 10px"
                        }}
                    >
                        <FaSave />
                    </Button>
                </Flex>
            </View>
        );
    };

    const renderTableTypeSelect = () => {
        if (!displayTableTypeSelect) {
            // Button to open table type select
            return (
                <Button onClick={() => updateDisplayTableTypeSelect(true)}>
                    {tableSelectedViewName ? tableSelectedViewName.toUpperCase() : "LOADING"} VIEW
                </Button>
            );
        }
    
        // Modal to delete the current view
        if (showDeleteViewForm) {
            if (tableSelectedViewCanDelete) {
                return (
                    <View>
                        <Heading level={4}>Delete Table View</Heading>
                        <Text>Are you sure you'd like to delete the {tableSelectedViewName} view?</Text>
                        <br />
                        <Button onClick={() => deleteView()}>CONFIRM</Button>
                        <br /><br />
                        <Button onClick={() => updateShowDeleteViewForm(false)}>CANCEL</Button>
                    </View>
                );
            } else {
                return (
                    <View>
                        <Heading level={4}>Cannot Delete</Heading>
                        <Text>The {tableSelectedViewName} view is shared and cannot be deleted.</Text>
                        <br />
                        <Button onClick={() => updateShowDeleteViewForm(false)}>OKAY</Button>
                    </View>
                );
            }
        }
    
        
        // Table view selector
        return (
            <View>
                <Flex alignItems="center" gap="0.5rem">
                    <Heading level={4}>{tableSelectedViewName}</Heading>
                    <Button
                        style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: isFavoriteView ? "default" : "pointer",
                            fontSize: "1.5rem",
                            color: isFavoriteView ? "gold" : "gray"
                        }}
                        onClick={() => {
                            if (!isFavoriteView) makeFavoriteView();
                        }}
                        title={isFavoriteView ? "Your favorite view is displayed by default" : "Click to make this your favorite view"}
                        aria-label={isFavoriteView ? "Favorite view" : "Make favorite"}
                    >
                        ★
                    </Button>
                </Flex>
                <Text>Select, create, and manage custom views</Text>
                <Text>
                    <i>Hint: Use the DEFAULT view to load all available columns.</i>
                </Text>
                <SelectField
                    value={tableSelectedViewId ?? ""}
                    onChange={(e) => {
                        updateIsLoading(true);
                        const selectedId = Number(e.target.value);
                        const selectedView = tableViews.find((view) => view.id === selectedId);
                        var isPivotEnabled = false;
                        var isFavorite = false
                        if(selectedView.pivot_mode_enabled > 0)
                        {
                            isPivotEnabled = true;
                        }
                        if(selectedView.is_favorite > 0)
                        {
                            isFavorite = true;
                        }
                        var canDeleteView = false;
                        if(selectedView.all_users === 0)
                        {
                            canDeleteView = true;
                        }
                        if (selectedView) {
                            applyView(selectedView);
                            updateIsLoading(false)
                        } else {
                            console.log('selectedView false');
                            updateIsLoading(false)
                        }
                    }}
                >
                    {[...tableViews]
                        .sort((a, b) => {
                            // 1. favorite first
                            if ((b.is_favorite || 0) !== (a.is_favorite || 0)) {
                                return (b.is_favorite || 0) - (a.is_favorite || 0);
                            }

                            // 2. Default (id = 1) next
                            if (a.id === 1 && b.id !== 1) return -1;
                            if (b.id === 1 && a.id !== 1) return 1;

                            // 3. alphabetical
                            return (a.title || "").localeCompare(b.title || "");
                        })
                        .map((view) => (
                            <option key={view.id} value={view.id}>
                                {view.title}
                            </option>
                        ))}
                </SelectField>
                <br />
                {tableSelectedViewCanDelete && (
                    <View>
                        <Button onClick={() => updateShowDeleteViewForm(true)}>DELETE VIEW</Button>
                        <br />
                        <br />
                    </View>
                )}
                <Button onClick={saveGridState}>SAVE CURRENT VIEW</Button>
                <br />
                <br />
                <Button onClick={() => updateDisplayTableTypeSelect(false)}>CLOSE</Button>
            </View>
        );
    }


    

    return(
        <View>
            {
                is_loading ? <Loadingmini /> : 
                <View>
                    {/*renderTableTypeSelect()*/}
                    <View>
                        {renderViewFolderTabs()}
                        <View style={{ paddingLeft: "10px", flexShrink: 0 }}>
                            <Button
                                onClick={saveGridState}
                                title="Save current view"
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "16px"
                                }}
                            >
                                <i className="fa fa-save" />
                            </Button>
                        </View>
                    </View>
                </View>
            }
            {
                showDeleteViewForm && (
                    <View style={modalStyles.overlay}>
                        <View style={modalStyles.modal}>
                            {tableSelectedViewCanDelete ? (
                                <>
                                    <Heading level={4}>Delete Table View</Heading>
                                    <Text>
                                        Are you sure you'd like to delete the {tableSelectedViewName} view?
                                    </Text>
                                    <br />
                                    <Button onClick={deleteView}>CONFIRM</Button>
                                    <br /><br />
                                    <Button onClick={() => updateShowDeleteViewForm(false)}>CANCEL</Button>
                                </>
                            ) : (
                                <>
                                    <Heading level={4}>Cannot Delete</Heading>
                                    <Text>
                                        The {tableSelectedViewName} view is shared and cannot be deleted.
                                    </Text>
                                    <br />
                                    <Button onClick={() => updateShowDeleteViewForm(false)}>OKAY</Button>
                                </>
                            )}
                        </View>
                    </View>
                )
            }
            {
                showSaveViewForm && (
                    <SaveViewModal
                        newViewName={newViewName}
                        setNewViewName={updateNewViewName}
                        onSubmit={saveView}
                        onCancel={() => updateShowSaveViewForm(false)}
                        errorTooLong={displayErrorColumnStateTooLong}
                        modalStyles={modalStyles}
                    />
            )}
        </View>
    );
}